import { useState, useEffect, useCallback, useRef } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { getInventoryDropdown, createOrder } from '../utils/api';
import { roundRupees } from '../utils/money';
import { useOrderFooterInset } from '../hooks/useOrderFooterInset';
import ModelPicker from './ModelPicker';
import OrderCustomerFieldsCollapsible from './OrderCustomerFieldsCollapsible';
import OrderFormStickyBar from './OrderFormStickyBar';
import RetailModelSidePanel from './RetailModelSidePanel';

const emptyItem = () => ({
  category: '',
  model: '',
  modelName: '',
  modelNumber: '',
  unitPrice: 0,
  currentStock: 0,
  qty: 1,
  totalValue: 0,
  advance: 0,
  colorRequest: '',
});

const RetailOrderPage = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [lastCreatedOrderReceipt, setLastCreatedOrderReceipt] = useState(null);
  const [phone, setPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [item, setItem] = useState(emptyItem());
  const [settledPrice, setSettledPrice] = useState('');
  const [panelModel, setPanelModel] = useState(null);
  const [pickerResetKey, setPickerResetKey] = useState(0);
  const formRef = useRef(null);
  const footerRef = useRef(null);

  useOrderFooterInset(formRef, footerRef);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const data = await getInventoryDropdown('retail');
        setInventory(data);
      } catch (error) {
        toast.error(error.message || 'Failed to load inventory');
      }
    };
    fetchInventory();
  }, []);

  useEffect(() => {
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');

    return () => {
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  const catalogGrandTotal = roundRupees(item.totalValue);
  const hasSettled =
    settledPrice !== '' &&
    settledPrice !== null &&
    settledPrice !== undefined &&
    Number.isFinite(Number(settledPrice));
  const grandTotal = hasSettled ? roundRupees(Number(settledPrice)) : catalogGrandTotal;
  const totalAdvance = roundRupees(item.advance);
  const balance = roundRupees(grandTotal - totalAdvance);

  const handleModelSelect = (selectedModel) => {
    setPanelModel(selectedModel);
  };

  const handleConfirmSelection = ({ advance, colorRequest, settledPrice: panelSettled }) => {
    if (!panelModel) return;

    const totalValue = roundRupees(panelModel.price);
    const nextSettled =
      panelSettled !== '' &&
      panelSettled !== null &&
      panelSettled !== undefined &&
      Number.isFinite(Number(panelSettled))
        ? roundRupees(Number(panelSettled))
        : '';
    const effectiveCap =
      nextSettled !== '' ? nextSettled : totalValue;
    const cappedAdvance = Math.min(Math.max(0, roundRupees(advance)), effectiveCap);

    setSettledPrice(nextSettled);
    setItem({
      category: panelModel.size,
      model: panelModel._id,
      modelName: panelModel.modelName,
      modelNumber: panelModel.modelNumber,
      unitPrice: totalValue,
      currentStock: panelModel.currentStock,
      qty: 1,
      totalValue,
      advance: cappedAdvance,
      colorRequest,
    });
    setPanelModel(null);
    toast.success('Model selected');
  };

  const handleClearSelection = () => {
    setItem(emptyItem());
    setSettledPrice('');
    setPanelModel(null);
  };

  const resetForm = useCallback(() => {
    setCustomerName('');
    setPhone('');
    setCustomerAddress('');
    setItem(emptyItem());
    setSettledPrice('');
    setPanelModel(null);
    setPickerResetKey((k) => k + 1);
  }, []);

  const handleClearForm = () => {
    resetForm();
    toast.success('Form cleared');
  };

  const handleSettledChange = (value) => {
    setSettledPrice(value);
    if (value === '') return;
    const settled = roundRupees(Number(value) || 0);
    setItem((prev) => {
      if (!prev.model) return prev;
      if (roundRupees(prev.advance) <= settled) return prev;
      return { ...prev, advance: settled };
    });
  };

  const handleSettledClear = () => {
    setSettledPrice('');
    setItem((prev) => {
      if (!prev.model) return prev;
      if (roundRupees(prev.advance) <= roundRupees(prev.totalValue)) return prev;
      return { ...prev, advance: roundRupees(prev.totalValue) };
    });
  };

  const handleAdvanceChange = (value) => {
    const next = roundRupees(value);
    setItem((prev) => ({
      ...prev,
      advance: Math.min(Math.max(0, next), grandTotal),
    }));
  };

  const handleAdvanceFull = () => {
    setItem((prev) => ({ ...prev, advance: grandTotal }));
  };

  const handleAdvanceClear = () => {
    setItem((prev) => ({ ...prev, advance: 0 }));
  };

  const validateForm = useCallback(() => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    const trimmedPhone = phone.trim();
    if (trimmedPhone && !/^[6-9]\d{9}$/.test(trimmedPhone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return false;
    }
    if (!item.model) {
      toast.error('Please select a model');
      return false;
    }
    const inventoryItem = inventory.find((inv) => inv._id === item.model);
    if (inventoryItem && !inventoryItem.isAvailable) {
      toast.error(`Cannot select ${inventoryItem.modelName} - item is out of stock`);
      return false;
    }
    if (hasSettled && Number(settledPrice) < 0) {
      toast.error('Agreed price cannot be negative');
      return false;
    }
    if (totalAdvance > grandTotal) {
      toast.error('Advance amount cannot exceed total order value');
      return false;
    }
    if (totalAdvance < 0) {
      toast.error('Advance amount cannot be negative');
      return false;
    }
    return true;
  }, [
    customerName,
    phone,
    item.model,
    inventory,
    totalAdvance,
    grandTotal,
    hasSettled,
    settledPrice,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        type: 'retail',
        customerName: customerName.trim(),
        phone: phone.trim(),
        customerAddress: customerAddress.trim(),
        items: [{
          model: item.model,
          qty: 1,
          advance: roundRupees(item.advance),
          colorRequest: item.colorRequest,
        }],
      };
      if (hasSettled) {
        payload.settledPrice = roundRupees(Number(settledPrice));
      }

      const result = await createOrder(payload);

      toast.success(`Retail order ${result.order.orderNo} created`);

      setLastCreatedOrderReceipt({
        orderId: result.order._id,
        orderNo: result.order.orderNo,
      });

      resetForm();
    } catch (error) {
      toast.error(error.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (panelModel) return;
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        if (!loading && validateForm()) {
          document.getElementById('retail-order-form')?.requestSubmit();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loading, validateForm, panelModel]);

  const existingPanelItem =
    item.model && panelModel?._id === item.model ? item : null;

  return (
    <>
      <Toaster position="top-right" />
      <div className="flex flex-col h-full min-h-0 overflow-hidden bg-gradient-to-br from-green-50 to-emerald-100 py-1 md:py-4">
        <div className="container mx-auto px-4 flex flex-col flex-1 min-h-0">
          <div className="max-w-6xl mx-auto flex flex-col flex-1 min-h-0 w-full">
            <div className="shrink-0 mb-2 space-y-2">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-2">
                <div className="shrink-0">
                  <h1 className="text-xl md:text-2xl font-bold text-gray-800">New Retail Order</h1>
                  <p className="hidden md:block text-xs text-gray-500">
                    Ctrl+Enter · Tap a model · optional agreed price in panel or footer
                  </p>
                </div>
                <div className="w-full lg:max-w-2xl lg:flex-1">
                  <OrderCustomerFieldsCollapsible
                    customerName={customerName}
                    phone={phone}
                    customerAddress={customerAddress}
                    onCustomerNameChange={setCustomerName}
                    onPhoneChange={setPhone}
                    onCustomerAddressChange={setCustomerAddress}
                    accentFocus="focus:ring-green-500 focus:border-green-500"
                    phoneRequired={false}
                  />
                </div>
              </div>
            </div>

            <form
              ref={formRef}
              id="retail-order-form"
              onSubmit={handleSubmit}
              className="order-form--mobile-overlay-footer bg-white rounded-lg shadow-xl flex-1 min-h-0 p-2 sm:p-6 md:grid"
            >
              <section className="flex flex-col min-h-0 overflow-hidden gap-2">
                <div className="hidden md:flex items-center justify-between gap-2 shrink-0">
                  <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Select model</h2>
                  {item.model && (
                    <span className="text-xs text-gray-500 text-right">
                      Qty 1 · ₹{item.totalValue.toLocaleString('en-IN')}
                    </span>
                  )}
                </div>
                <ModelPicker
                  inventory={inventory}
                  fillHeight
                  compactMobile
                  dynamicScrollInset
                  resetKey={pickerResetKey}
                  selectedModelId={item.model}
                  onSelect={handleModelSelect}
                  accentRing="ring-green-500"
                />
              </section>

              <OrderFormStickyBar
                ref={footerRef}
                variant="inCard"
                grandTotal={grandTotal}
                catalogGrandTotal={catalogGrandTotal}
                totalAdvance={totalAdvance}
                balance={balance}
                loading={loading}
                submitDisabled={!item.model}
                submitLabel="Create Retail Order"
                submitClassName="admin-btn admin-btn-submit h-9 px-4 text-sm font-semibold rounded-md"
                lastCreatedOrderReceipt={lastCreatedOrderReceipt}
                onClearForm={handleClearForm}
                showSettledInput={Boolean(item.model)}
                settledValue={settledPrice}
                onSettledChange={handleSettledChange}
                onSettledClear={handleSettledClear}
                showAdvanceInput={Boolean(item.model)}
                advanceValue={totalAdvance}
                onAdvanceChange={handleAdvanceChange}
                onAdvanceFull={handleAdvanceFull}
                onAdvanceClear={handleAdvanceClear}
              />
            </form>
          </div>
        </div>
      </div>

      <RetailModelSidePanel
        item={panelModel}
        open={Boolean(panelModel)}
        existingItem={existingPanelItem}
        existingSettledPrice={settledPrice}
        onClose={() => setPanelModel(null)}
        onConfirm={handleConfirmSelection}
        onClearSelection={handleClearSelection}
      />
    </>
  );
};

export default RetailOrderPage;
